import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Circle, Sparkles, ArrowRight, X } from "lucide-react";

const ITEMS = [
  { key: "child_added",        label: "Add your child",     to: "/app/children?spotlight=add-child-button" },
  { key: "coparent_invited",   label: "Invite co-parent",   to: "/app/settings?spotlight=invite-section" },
  { key: "first_event_added",  label: "Add first event",    to: "/app/calendar?spotlight=add-event-button" },
  { key: "first_contact_visit",label: "Record first contact visit", to: "/app/contact-visits?spotlight=cv-fab-new" },
  { key: "first_expense_added", label: "Log first expense", to: "/app/expenses?spotlight=add-expense-button" },
];

export default function OnboardingChecklist({ status, onDismiss }) {
  const [hiding, setHiding] = useState(false);
  const [hidden, setHidden] = useState(false);
  if (!status?.show_checklist || hidden) return null;
  const steps = status.steps || {};
  const doneCount = ITEMS.filter((it) => steps[it.key]).length;

  const dismissChecklist = async () => {
    // Optimistic hide — request is fire-and-forget; if it fails the card
    // just reappears on next load, which is fine.
    setHiding(true);
    setHidden(true);
    try { await api.post("/onboarding/hide-checklist"); } catch (err) { console.warn("[onboarding] hide-checklist failed:", err?.response?.status || err?.message); }
    if (onDismiss) onDismiss();
  };

  return (
    <section
      className="relative overflow-hidden rounded-md border-2 border-dashed border-[#A7D8DA] bg-gradient-to-br from-[#F0F8F8] via-[#F4FAFA] to-[#EAF5F5]"
      data-testid="onboarding-checklist"
    >
      {/* Decorative corner sparkle — reinforces "helper" feel without taking space. */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#D6F0EF] opacity-60" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-[#E0F2F2] opacity-40" />

      <header className="relative flex flex-row items-center justify-between gap-3 px-5 py-3 border-b border-dashed border-[#A7D8DA]/70">
        <div className="inline-flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-sm bg-white text-[#116B6E] border border-[#A7D8DA] inline-flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <h2 className="font-heading text-base sm:text-lg font-medium text-[#2E3A3F] truncate">Finish setting up your account</h2>
        </div>
        <div className="inline-flex items-center gap-2 flex-shrink-0">
          <span
            className="text-[10px] uppercase tracking-[0.1em] border border-[#1F9FA4] bg-white text-[#116B6E] px-2 py-0.5 rounded-sm font-medium tabular-nums"
            data-testid="onboarding-checklist-progress"
          >
            {doneCount} / {ITEMS.length}
          </span>
          <button
            type="button"
            onClick={dismissChecklist}
            disabled={hiding}
            aria-label="Dismiss setup checklist"
            title="Dismiss — you can always pick it back up from Settings"
            className="w-7 h-7 rounded-sm text-[#6F828A] hover:text-[#2E3A3F] hover:bg-white/70 inline-flex items-center justify-center transition-colors"
            data-testid="onboarding-checklist-dismiss"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>
      </header>

      <ul className="relative divide-y divide-dashed divide-[#A7D8DA]/60">
        {ITEMS.map((it) => {
          const done = !!steps[it.key];
          return (
            <li key={it.key}>
              <Link
                to={it.to}
                className={`flex items-center gap-3 px-5 py-3 transition-colors ${done ? "text-[#6F828A] hover:bg-white/50" : "text-[#2E3A3F] hover:bg-white/70"}`}
                data-testid={`onboarding-checklist-${it.key}`}
                data-done={done ? "true" : "false"}
              >
                {done ? (
                  <span className="w-5 h-5 rounded-full bg-[#1F9FA4] text-white inline-flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                ) : (
                  <Circle className="w-5 h-5 text-[#A7D8DA] flex-shrink-0" strokeWidth={1.5} />
                )}
                <span className={`flex-1 text-sm ${done ? "line-through" : "font-medium"}`}>{it.label}</span>
                {!done && <ArrowRight className="w-4 h-4 text-[#6F828A]" strokeWidth={1.5} />}
              </Link>
            </li>
          );
        })}
      </ul>

      <footer className="relative px-5 py-3 border-t border-dashed border-[#A7D8DA]/70 flex flex-col gap-2">
        <p className="text-[11px] text-[#116B6E]" data-testid="onboarding-checklist-trial-note">
          You're on the <strong>30-day free trial</strong> — full access to every day-to-day feature.
          Exports, downloads, Court Bundle generation and external record sharing unlock when you subscribe.
        </p>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-[#6F828A]">Resume the guided setup any time.</span>
          <Link
            to="/app/onboarding"
            className="text-sm font-medium text-[#116B6E] hover:text-[#0F5A5C] inline-flex items-center gap-1"
            data-testid="onboarding-checklist-resume"
          >
            Continue setup <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </footer>
    </section>
  );
}
